import type { RoomOptionsContainer } from '@elara/client-core/containers/RoomOptionsContainer';

import { useApp, useHotelRoomSearchState, useTripsState } from '#states/hooks.js';
import { Button } from '#ui/button.js';

/**
 * "View all rooms" under the concierge's room cards (ELARA-1092): opens the hotel guide's Rooms tab
 * on the search those rooms came from, listing every room it saved — not only the few presented.
 *
 * Reads the search from one presented room (every room of a search carries its `searchId` and
 * `hotelId`), and opens the SAVED results: no new search runs, so the guide shows exactly what the
 * concierge saw, with a Refresh there to re-price it. Renders nothing for a room saved before
 * searches carried an id.
 */
export function ViewAllRoomResultsButton({ roomOptionId }: { roomOptionId: string }) {
  const tripsState = useTripsState();
  const roomSearch = useHotelRoomSearchState();
  const { features } = useApp('features');
  const { roomOptions } = features as { roomOptions: RoomOptionsContainer };
  const tripId = tripsState.current?.id;
  if (!tripId) return null;

  const room = roomOptions.ClientRoomOption.get({ tripId, id: roomOptionId }).data;
  const searchId = room?.searchId;
  const hotelId = room?.hotelId;
  if (!searchId || !hotelId) return null;

  const open = () => {
    roomSearch.requestOpen({ hotelId, searchId });
    tripsState.showGuide('hotel', hotelId, { tab: 'rooms', search: searchId });
  };

  return (
    <Button variant="link" size="sm" onClick={open} className="self-start">
      View all rooms
    </Button>
  );
}
